import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {Splat, SplatsService} from '../splats.service';
import {combineLatest, distinctUntilChanged, firstValueFrom, from, merge, Observable, of, Subject, switchMap} from 'rxjs';
import {FrequencyViewChartService} from '../frequency-view-chart.service';
import {debounceTime, filter as filterRxjs, map} from 'rxjs/operators';
import {
  AsaxFrequencyViewService,
  AsaxHeaderService,
  AsaxResultTableService,
  AsaxSkyViewService,
  Extremes,
  Observation,
  Project,
  Publication
} from 'asax-common';
import {FrequencyViewChartFilterService} from '../frequency-view-chart-filters/frequency-view-chart-filter.service';
import {FrequencyService} from '../frequency.service';
import {AsaxDownloadingService} from 'asax-downloading';

const headerSearchFiltersMappingSearchProperties = {
  // Header search filters mapping search properties
  //    Position
  sourceNameResolver: {property: 'footprint'},
  sourceName: {property: 'sourceName'},
  raDec: {property: {ra: 'ra', dec: 'dec', footprint: 'footprint'}},
  galactic: {
    property: {
      longitude: 'galacticLongitude',
      latitude: 'galacticLatitude',
      ra: 'ra',
      dec: 'dec',
      footprint: 'footprint'
    }
  },
  ecliptic: {
    property: {
      longitude: 'eclipticLongitude',
      latitude: 'eclipticLatitude',
      ra: 'ra',
      dec: 'dec',
      footprint: 'footprint'
    }
  },
  targetList: {property: {sourceName: 'sourceName', ra: 'ra', dec: 'dec', footprint: 'footprint'}},
  spatialResolution: {property: 'spatialResolution'},
  largestAngularScale: {property: 'largestAngularScale'},

  //    Energy
  frequency: {
    path: 'energy',
    property: {frequencyMin: 'energy.freqMin', frequencyMax: 'energy.freqMax'},
    isNested: true
  },
  band: {property: 'band'},
  spectralResolution: {path: 'energy', property: 'energy.resMin', isNested: true},
  continuumSensitivity: {property: 'continuumSensitivity'},
  lineSensitivity: {property: 'lineSensitivity'},

  //    Project
  projectCode: {property: 'project.code'},
  projectTitle: {property: 'project.title'},
  projectAbstract: {property: 'project.proposalAbstract'},
  piName: {property: 'project.piName'},
  proposalAuthors: {property: 'project.coiName'},
  scienceKeywordIds: {property: 'project.scienceKeywordIds'},

  //    Publication
  bibCode: {path: 'publications', property: 'publications.bibCode', isNested: true},
  title: {path: 'publications', property: 'publications.title', isNested: true},
  bibAbstract: {path: 'publications', property: 'publications.bibAbstract', isNested: true},
  firstAuthor: {path: 'publications', property: 'publications.firstAuthor', isNested: true},
  authors: {path: 'publications', property: 'publications.authors', isNested: true},

  //    Observations
  observationDate: {property: 'observationDate'},
  polarisationType: {path: 'energy', property: 'energy.polNum', isNested: true},
  mous: {property: 'mous'},
  publicData: {property: 'releaseDate'},
  objectTypes: {
    objectTypesSimbad: {longtype: 'longtypesSimbad', isBestMatch: 'bestLongtypeSimbad'},
    objectTypesNed: {longtype: 'longtypesNed', isBestMatch: 'bestLongtypeNed'}
  },
  calibratorObservations: {property: 'isScience'}
};
const observationsColumnsSearchFiltersMappingSearchProperties = {
  observationsProjectCode: {property: 'project.code'},
  observationsSourceName: {property: 'sourceName'},
  observationsRa: {property: 'ra'},
  observationsDec: {property: 'dec'},
  observationsBand: {property: 'band'},
  observationsContinuumSensitivity: {property: 'continuumSensitivity'},
  observationsFrequency: {
    path: 'energy',
    property: {frequencyMin: 'energy.freqMin', frequencyMax: 'energy.freqMax'},
    isNested: true
  },
  observationsReleaseDate: {property: 'releaseDate'},
  observationsNumberPublications: {property: 'numberPublications'},
  observationsSpatialResolution: {property: 'spatialResolution'},
  observationsVelocityResolution: {property: 'velocityResolution'},
  observationsArray: {property: 'array'},
  observationsMosaic: {property: 'mosaic'},
  observationsLargestAngularScale: {property: 'largestAngularScale'},
  observationsFov: {property: 'fov'},
  observationsScientificCategory: {property: 'project.scientificCategory'},
  observationsScienceKeyword: {property: 'project.scienceKeyword'},
  observationsIntegration: {property: 'integration'},
  observationsGalacticLongitude: {property: 'galacticLongitude'},
  observationsGalacticLatitude: {property: 'galacticLatitude'},
  observationsEclipticLongitude: {property: 'eclipticLongitude'},
  observationsEclipticLatitude: {property: 'eclipticLatitude'},
  observationsFrequencyResolution: {property: 'frequencyResolution'},
  observationsPolProducts: {property: 'polProducts'},
  observationsBaselineL80: {property: 'baselineL80'},
  observationsBaselineL5: {property: 'baselineL5'},
  observationsObservationdDate: {property: 'observationDate'},
  observationsPiName: {property: 'project.piName'},
  observationsSbName: {property: 'sbName'},
  observationsProposalAuthors: {property: 'project.coiName'},
  observationsPwv: {property: 'pwv'},
  observationsGous: {property: 'gous'},
  observationsNumberAsdms: {property: 'numberAsdms'},
  observationsProjectTitle: {property: 'project.title'},
  observationsProjectType: {property: 'project.type'},
  observationsScanIntent: {property: 'scanIntent'},
  observationsQa2Status: {property: 'qa2Status'},
  observationsExternalCollections: {property: 'externalCollections'}
};
const projectsColumnsSearchFiltersMappingSearchProperties = {
  projectsCode: {property: 'project.code'},
  projectsTitle: {property: 'project.title'},
  projectsType: {property: 'project.type'},
  projectsPiName: {property: 'project.piName'},
  projectsProposalAuthors: {property: 'project.coiName'},
  projectsMaxReleaseDate: {property: 'project.releaseDate'},
  projectsNumberPublications: {property: 'project.numberPublications'},
  projectsNumberObservations: {property: 'project.numberObservations'},
  projectsSbNames: {property: 'project.sbNames'}
};
const publicationsColumnsSearchFiltersMappingSearchProperties = {
  publicationsBibCode: {path: 'publications', property: 'publications.bibCode', isNested: true},
  publicationsFirstAuthor: {path: 'publications', property: 'publications.firstAuthor', isNested: true},
  publicationsJournal: {path: 'publications', property: 'publications.journal', isNested: true},
  publicationsYear: {path: 'publications', property: 'publications.publicationYear', isNested: true},
  publicationsTitle: {path: 'publications', property: 'publications.title', isNested: true},
  publicationsMaxReleaseDate: {path: 'publications', property: 'publications.releaseDate', isNested: true},
  publicationsNumberProjects: {path: 'publications', property: 'publications.numberProjects', isNested: true},
  publicationsNumberObservations: {path: 'publications', property: 'publications.numberObservations', isNested: true},
  publicationsAuthors: {path: 'publications', property: 'publications.authors', isNested: true}
};

@Component({
  selector: 'asax-frequency-view-frequency-view-chart-container',
  templateUrl: './frequency-view-chart-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FrequencyViewChartContainerComponent {
  @Input() height: number;
  @Input() width: number;

  extremes: Observable<Extremes>;
  splats: Observable<Splat[]>;
  footprintHovered: Observable<any>;
  frequenciesIntervalsRowHovered: Observable<any[]>;
  frequency: Observable<any[]>;
  chartFiltersContainerHeight: number;

  chartWidthSubject = new Subject<number>();
  extremesSubject = new Subject<Extremes>();
  selectedFrequencies: Observable<any>;

  heightFrequencyChartFiltersContainer: number;
  displayResetBoundariesButton: boolean = false;

  constructor(private readonly asaxHeaderService: AsaxHeaderService,
              private readonly asaxResultTableService: AsaxResultTableService,
              private readonly asaxFrequencyViewService: AsaxFrequencyViewService,
              private readonly asaxSkyViewService: AsaxSkyViewService,
              private readonly asaxDownloadingService: AsaxDownloadingService,
              private readonly frequencyViewChartService: FrequencyViewChartService,
              private readonly frequencyViewChartFilterService: FrequencyViewChartFilterService,
              private readonly frequencyService: FrequencyService,
              private readonly splatsService: SplatsService) {
    this.splats = combineLatest([
      this.chartWidthSubject.asObservable().pipe(distinctUntilChanged()),
      this.extremesSubject.asObservable(),
      this.frequencyViewChartFilterService.getChartFilters()
    ]).pipe(
      debounceTime(500),
      switchMap(([chartWidth, extremes, filters]) => this.splatsService.fetchSplats(chartWidth, extremes, filters))
    );

    this.frequency = combineLatest([
      this.asaxHeaderService.getHeaderFilters(),
      this.asaxResultTableService.getColumnsFiltersForActiveTabSubject()
    ]).pipe(
      debounceTime(1000),
      switchMap(([filters, columnsFilters]) => {
        const filtersMappingSearchProperties = {
          ...headerSearchFiltersMappingSearchProperties,
          ...observationsColumnsSearchFiltersMappingSearchProperties,
          ...projectsColumnsSearchFiltersMappingSearchProperties,
          ...publicationsColumnsSearchFiltersMappingSearchProperties
        };
        return this.frequencyService.fetchFrequencies([...filters, ...columnsFilters], filtersMappingSearchProperties);
      })
    );

    this.extremes = this.asaxFrequencyViewService.getExtremes();
    this.footprintHovered = this.asaxSkyViewService.getFootprintHovered().pipe(
      debounceTime(500),
      switchMap(footprint => this.frequencyService.fetchFrequenciesForFootprint(footprint))
    );

    this.frequenciesIntervalsRowHovered = combineLatest([
      this.asaxResultTableService.getRowHovered(),
      this.asaxResultTableService.getActiveTab()
    ]).pipe(
      debounceTime(250),
      switchMap(([rowHovered, activeTab]) => {
          if (rowHovered) {
            switch (activeTab.id) {
              case 'observations': {
                return this.frequencyService.fetchFrequenciesIntervalsForObservation((<Observation>rowHovered).datasetId).pipe(
                  map(intervals => intervals?.map(interval => ({
                    ...interval,
                    selected: asaxDownloadingService.isObservationSelectedToBeDownloaded((<Observation>rowHovered).datasetId)
                  })))
                );
                break;
              }
              case 'projects': {
                return this.frequencyService.fetchFrequenciesIntervalsForProject((<Project>rowHovered).code).pipe(
                  map(intervals => intervals?.map(interval => ({
                    ...interval,
                    selected: asaxDownloadingService.isProjectSelectedToBeDownloaded((<Project>rowHovered).code)
                  })))
                );
                break;
              }
              case 'publications': {
                return this.frequencyService.fetchFrequenciesIntervalsForPublication((<Publication>rowHovered).bibCode).pipe(
                  map(intervals => intervals?.map(interval => ({
                    ...interval,
                    selected: asaxDownloadingService.isPublicationSelectedToBeDownloaded((<Publication>rowHovered).bibCode)
                  })))
                );
                break;
              }
            }
          } else {
            return of([]);
          }
        }
      )
    );

    this.selectedFrequencies = merge(
      combineLatest([
        this.asaxDownloadingService.getObservationsToBeDownloaded(),
        this.asaxResultTableService.getActiveTab()
      ]).pipe(
        filterRxjs(([rowIdsToBeDownloaded, activeTab]) => activeTab.id === 'observations'),
      ),
      combineLatest([
        this.asaxDownloadingService.getProjectsToBeDownloaded(),
        this.asaxResultTableService.getActiveTab()
      ]).pipe(
        filterRxjs(([rowIdsToBeDownloaded, activeTab]) => activeTab.id === 'projects'),
      ),
      combineLatest([
        this.asaxDownloadingService.getPublicationsToBeDownloaded(),
        this.asaxResultTableService.getActiveTab()
      ]).pipe(
        filterRxjs(([rowIdsToBeDownloaded, activeTab]) => activeTab.id === 'publications'),
      )
    ).pipe(
      switchMap(([rowIdsToBeDownloaded, activeTab]) => {
        if (rowIdsToBeDownloaded?.length > 0) {
          switch (activeTab.id) {
            case 'observations': {
              return from(Promise.all(
                rowIdsToBeDownloaded.map(obsId => this.getIntervalsForObservation(obsId))
              ));
            }
            case 'projects': {
              return from(Promise.all(
                rowIdsToBeDownloaded.map(projId => this.getIntervalsForProjects(projId))
              ));
            }
            case 'publications': {
              return from(Promise.all(
                rowIdsToBeDownloaded.map(publicationId => this.getIntervalsForPublications(publicationId))
              ));
            }
          }
        } else {
          return of([]);
        }
      })
    );
  }

  getIntervalsForObservation(obsId: string) {
    return firstValueFrom(this.frequencyService.fetchFrequenciesIntervalsForObservation(obsId).pipe(
      map(intervals => intervals?.map(interval => ({
        ...interval,
        selected: this.asaxDownloadingService.isObservationSelectedToBeDownloaded(obsId)
      })))));
  }

  getIntervalsForProjects(projId: string) {
    return firstValueFrom(this.frequencyService.fetchFrequenciesIntervalsForProject(projId).pipe(
      map(intervals => intervals?.map(interval => ({
        ...interval,
        selected: this.asaxDownloadingService.isProjectSelectedToBeDownloaded(projId)
      })))));
  }

  getIntervalsForPublications(publicationId: string) {
    return firstValueFrom(this.frequencyService.fetchFrequenciesIntervalsForPublication(publicationId).pipe(
      map(intervals => intervals?.map(interval => ({
        ...interval,
        selected: this.asaxDownloadingService.isPublicationSelectedToBeDownloaded(publicationId)
      })))));
  }

  onOutFrequencyChartMouseMove($event: any): void {
    this.frequencyViewChartService.setMouseEventPlotLineOrBand($event);
  }

  onOutSplatHovered(splat: Splat): void {
    this.splatsService.setSplatHovered(splat);
  }

  onOutChartWidth(chartWidth: number): void {
    this.chartWidthSubject.next(chartWidth);
  }

  onOutExtremes(extremes: Extremes): void {
    this.extremesSubject.next(extremes);
  }
}
